import React, { useEffect, useMemo, useState } from 'react';
import { fetchData, postData } from '../components/misc';
import { trackEvent } from '../utils/bridge.js';

const countries = ['Kenya', 'Tanzania', 'Uganda'];
const packageOptions = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Emerald', 'Ruby', 'Sapphire', 'EXPIRED'];
const defaultTargetPackages = packageOptions.filter((item) => item !== 'Sapphire' && item !== 'EXPIRED');

const parseEmailPaste = (text) => String(text || '')
  .split(/[\s,;]+/)
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

const manLabel = (man) => {
  if (!man) return '';
  return [man.fullname1, man.EMAIL, man.country1, man.package1].filter(Boolean).join(' - ');
};

const nameToInitials = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || 'Selected man';

export default function ScheduleMessages() {
  const [men, setMen] = useState([]);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [search, setSearch] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedMan, setSelectedMan] = useState(null);
  const [selectedRecipientEmail, setSelectedRecipientEmail] = useState('');
  const [compatibilityReport, setCompatibilityReport] = useState('');
  const [compatibilityPercentage, setCompatibilityPercentage] = useState('94');
  const [minAge, setMinAge] = useState('18');
  const [maxAge, setMaxAge] = useState('45');
  const [country, setCountry] = useState('Kenya');
  const [targetPackages, setTargetPackages] = useState(defaultTargetPackages);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedPreviewRecipients, setSelectedPreviewRecipients] = useState([]);
  const [pastedConfirmEmails, setPastedConfirmEmails] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loadingMen, setLoadingMen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingCompatibilityEmail, setLoadingCompatibilityEmail] = useState(false);
  const [compatibilityButtonState, setCompatibilityButtonState] = useState('idle');

  useEffect(() => {
    setLoadingMen(true);
    fetchData('/api/sirri_api/get_men_normal?limit=10000')
      .then((data) => {
        if (data?.responsecode === 200) {
          setMen(data.acc || []);
        } else {
          setError('Failed to load men.');
        }
      })
      .catch((err) => {
        console.error('Failed to load men', err);
        setError('Failed to load men.');
      })
      .finally(() => setLoadingMen(false));

    fetchData('/api/sirri_api/notifications/recipients')
      .then((data) => {
        if (data?.responsecode === 200) {
          setRecipientEmails(data.recipients || []);
        }
      })
      .catch((err) => console.error('Failed to load recipient emails', err));
  }, []);

  const filteredMen = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return men.slice(0, 50);
    return men.filter((man) => {
      const haystack = [man.fullname1, man.EMAIL, man.country1, man.city1, man.package1]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    }).slice(0, 50);
  }, [men, search]);

  const filteredRecipientEmails = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    const source = q ? recipientEmails.filter((email) => email.toLowerCase().includes(q)) : recipientEmails;
    return source.slice(0, 50);
  }, [recipientEmails, recipientSearch]);

  const resetPreview = () => {
    setPreview(null);
    setSelectedPreviewRecipients([]);
    setPastedConfirmEmails('');
    setResult(null);
    setError('');
  };

  const payload = (extra = {}) => ({
    man_email: selectedMan?.EMAIL || '',
    man_name: selectedMan?.fullname1 || '',
    min_age: minAge,
    max_age: maxAge,
    country,
    target_packages: targetPackages,
    message,
    ...extra,
  });

  const validate = (requireMessage = true) => {
    if (!selectedMan) {
      setError('Select a man first.');
      return false;
    }
    if (!country) {
      setError('Select a country.');
      return false;
    }
    if (targetPackages.length === 0) {
      setError('Select at least one package.');
      return false;
    }
    if (requireMessage && !message.trim()) {
      setError('Write the message to send.');
      return false;
    }
    const min = Number(minAge);
    const max = Number(maxAge);
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 18 || max < min) {
      setError('Enter a valid age range.');
      return false;
    }
    return true;
  };

  const togglePackage = (packageName) => {
    setTargetPackages((current) => {
      if (current.includes(packageName)) {
        return current.filter((item) => item !== packageName);
      }
      return [...current, packageName];
    });
    resetPreview();
  };

  const handlePreview = async (followUpOnly = false) => {
    trackEvent(followUpOnly ? 'admin_schedule_messages_follow_up_preview_pressed' : 'admin_schedule_messages_preview_pressed');
    resetPreview();
    if (!validate(!followUpOnly)) return;
    setLoadingPreview(true);
    try {
      const data = await postData('/api/sirri_api/schedule_messages/preview', payload({ follow_up_only: followUpOnly }));
      if (data?.responsecode === 200) {
        trackEvent('admin_schedule_messages_preview_success', { count: data.recipients?.length || 0, follow_up_only: followUpOnly });
        setPreview(data);
        setSelectedPreviewRecipients((data.recipients || []).map((recipient) => recipient.email));
      } else {
        setError(data?.message || 'Preview failed.');
      }
    } catch (err) {
      console.error('Failed to preview schedule messages', err);
      setError('Preview failed.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async () => {
    trackEvent('admin_schedule_messages_confirm_pressed');
    setError('');
    if (!preview || !validate()) return;
    const pastedEmails = parseEmailPaste(pastedConfirmEmails);
    const confirmEmails = pastedEmails.length > 0 ? pastedEmails : selectedPreviewRecipients;
    if (confirmEmails.length === 0) {
      setError('Select at least one recipient.');
      return;
    }
    setLoadingConfirm(true);
    try {
      const data = await postData('/api/sirri_api/schedule_messages/confirm', payload({
        follow_up_only: preview?.criteria?.follow_up_only || false,
        selected_recipient_emails: confirmEmails,
      }));
      if (data?.responsecode === 200) {
        trackEvent('admin_schedule_messages_confirm_success', { created: data.created_count || 0 });
        setResult(data);
        setPreview(null);
        setSelectedPreviewRecipients([]);
        setPastedConfirmEmails('');
      } else {
        setError(data?.message || 'Confirm failed.');
      }
    } catch (err) {
      console.error('Failed to confirm schedule messages', err);
      setError('Confirm failed.');
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleSendCompatibilityEmail = async () => {
    trackEvent('admin_schedule_messages_compatibility_email_pressed');
    setError('');
    setResult(null);
    const flashCompatibilityButton = (state) => {
      setCompatibilityButtonState(state);
      window.setTimeout(() => setCompatibilityButtonState('idle'), 1000);
    };
    if (!selectedMan) {
      setError('Select a man first.');
      flashCompatibilityButton('error');
      return;
    }
    if (!selectedRecipientEmail) {
      setError('Select a recipient email first.');
      flashCompatibilityButton('error');
      return;
    }
    if (!compatibilityReport.trim()) {
      setError('Paste the Full Match Compatibility Report first.');
      flashCompatibilityButton('error');
      return;
    }
    if (!compatibilityPercentage.trim()) {
      setError('Enter the compatibility percentage first.');
      flashCompatibilityButton('error');
      return;
    }
    setLoadingCompatibilityEmail(true);
    try {
      const data = await postData('/api/sirri_api/schedule_messages/compatibility_email', {
        man_email: selectedMan?.EMAIL || '',
        man_name: selectedMan?.fullname1 || '',
        recipient_email: selectedRecipientEmail,
        compatibility_report: compatibilityReport,
        compatibility_percentage: compatibilityPercentage,
      });
      if (data?.responsecode === 200) {
        trackEvent('admin_schedule_messages_compatibility_email_success');
        setResult({ compatibilityEmail: true, sent_to: data.sent_to, subject: data.subject });
        flashCompatibilityButton('success');
      } else {
        setError(data?.message || 'Compatibility email failed.');
        flashCompatibilityButton('error');
      }
    } catch (err) {
      console.error('Failed to send compatibility email', err);
      setError('Compatibility email failed.');
      flashCompatibilityButton('error');
    } finally {
      setLoadingCompatibilityEmail(false);
    }
  };

  const startAgain = () => {
    setPreview(null);
    setSelectedPreviewRecipients([]);
    setPastedConfirmEmails('');
    setResult(null);
    setError('');
  };

  const togglePreviewRecipient = (email) => {
    setSelectedPreviewRecipients((current) => {
      if (current.includes(email)) {
        return current.filter((item) => item !== email);
      }
      return [...current, email];
    });
  };

  const selectAllPreviewRecipients = () => {
    setSelectedPreviewRecipients((preview?.recipients || []).map((recipient) => recipient.email));
  };

  const clearPreviewRecipients = () => {
    setSelectedPreviewRecipients([]);
  };

  const compatibilityButtonClass = compatibilityButtonState === 'success'
    ? 'bg-green-700'
    : compatibilityButtonState === 'error'
      ? 'bg-red-700'
      : 'bg-blue-700 hover:bg-blue-600';

  return (
    <div className="max-w-5xl mx-auto space-y-4 text-white">
      <div className="bg-gray-900 rounded shadow p-4">
        <h1 className="text-xl font-semibold">Schedule messages</h1>
        <p className="text-sm text-gray-300">Select one man, target female users by age, country, and package, preview the recipients, then create staggered message tasks.</p>
      </div>

      <div className="bg-gray-900 rounded shadow p-4 space-y-4">
        <div>
          <label className="block text-sm mb-1">Search and select man</label>
          <input
            className="w-full text-white rounded p-2"
            placeholder="Search by name, email, country, city, package"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {selectedMan ? (
            <p className="mt-2 text-green-300 text-sm">Selected: {manLabel(selectedMan)}</p>
          ) : null}
        </div>

        <div className="max-h-72 overflow-y-auto grid md:grid-cols-2 gap-2">
          {loadingMen ? <p>Loading men...</p> : filteredMen.map((man, idx) => (
            <button
              key={`${man.EMAIL || man.fullname1}-${idx}`}
              className={`text-left rounded p-2 ${selectedMan === man ? 'bg-green-800' : 'bg-gray-800 hover:bg-gray-700'}`}
              onClick={() => { setSelectedMan(man); resetPreview(); }}
            >
              <div className="font-semibold">{man.fullname1 || 'Unnamed'}</div>
              <div className="text-xs text-gray-300">{[man.EMAIL, man.age1, man.country1, man.city1, man.package1].filter(Boolean).join(' - ')}</div>
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <label className="flex flex-col text-sm">Minimum age
            <input className="mt-1 text-white rounded p-2" type="number" value={minAge} onChange={(e) => { setMinAge(e.target.value); resetPreview(); }} />
          </label>
          <label className="flex flex-col text-sm">Maximum age
            <input className="mt-1 text-white rounded p-2" type="number" value={maxAge} onChange={(e) => { setMaxAge(e.target.value); resetPreview(); }} />
          </label>
          <label className="flex flex-col text-sm">Country
            <select className="mt-1 text-white rounded p-2" value={country} onChange={(e) => { setCountry(e.target.value); resetPreview(); }}>
              {countries.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="block text-sm">Target packages</label>
            <div className="flex gap-2 text-xs">
              <button className="bg-gray-800 px-2 py-1 rounded" type="button" onClick={() => { setTargetPackages(packageOptions); resetPreview(); }}>Select all</button>
              <button className="bg-gray-800 px-2 py-1 rounded" type="button" onClick={() => { setTargetPackages([]); resetPreview(); }}>Clear</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
            {packageOptions.map((packageName) => (
              <label key={packageName} className={`flex items-center gap-2 rounded p-2 text-sm ${targetPackages.includes(packageName) ? 'bg-green-800' : 'bg-gray-800'}`}>
                <input
                  type="checkbox"
                  checked={targetPackages.includes(packageName)}
                  onChange={() => togglePackage(packageName)}
                />
                {packageName}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400">Default keeps the previous behavior: all packages except Sapphire. Add or remove packages before previewing recipients.</p>
        </div>

        <label className="flex flex-col text-sm">Message
          <textarea
            className="mt-1 text-white rounded p-2 min-h-32"
            value={message}
            onChange={(e) => { setMessage(e.target.value); resetPreview(); }}
            placeholder="Write the message to send from the selected man"
          />
        </label>

        {error ? <div className="bg-red-900 text-red-200 p-2 rounded">{error}</div> : null}
        {result?.compatibilityEmail ? <div className="bg-green-900 text-green-200 p-2 rounded">Sent compatibility email to {result.sent_to}: {result.subject}</div> : null}
        {result && !result.compatibilityEmail ? <div className="bg-green-900 text-green-200 p-2 rounded">Created {result.created_count} scheduled message tasks.</div> : null}

        <div className="flex flex-wrap gap-2">
          <button className="bg-blue-700 px-4 py-2 rounded" onClick={() => handlePreview(false)} disabled={loadingPreview}>
            {loadingPreview ? 'Previewing...' : 'Preview recipients'}
          </button>
          <button className="bg-purple-700 px-4 py-2 rounded" onClick={() => handlePreview(true)} disabled={loadingPreview}>
            {loadingPreview ? 'Previewing...' : 'Preview recipients for follow up'}
          </button>
        </div>
      </div>

      <div className="bg-gray-900 rounded shadow p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Send match compatibility email</h2>
          <p className="text-sm text-gray-300">Use the selected man above, choose one recipient email, then send the HTML compatibility request email.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Selected man</label>
            <div className="bg-gray-800 rounded p-2 text-sm min-h-10">{selectedMan ? manLabel(selectedMan) : 'Select a man above first.'}</div>
          </div>
          <div>
            <label className="block text-sm mb-1">Search recipient email</label>
            <input
              className="w-full text-white rounded p-2"
              placeholder="Search registered recipient email"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
            />
            {selectedRecipientEmail ? <p className="mt-2 text-green-300 text-sm">Selected recipient: {selectedRecipientEmail}</p> : null}
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto grid md:grid-cols-2 gap-2">
          {filteredRecipientEmails.map((email) => (
            <button
              key={email}
              className={`text-left rounded p-2 text-sm ${selectedRecipientEmail === email ? 'bg-green-800' : 'bg-gray-800 hover:bg-gray-700'}`}
              onClick={() => setSelectedRecipientEmail(email)}
            >
              {email}
            </button>
          ))}
          {filteredRecipientEmails.length === 0 ? <p className="text-sm text-gray-300">No matching recipient emails.</p> : null}
        </div>
        <label className="flex flex-col text-sm">Full Match Compatibility Report
          <textarea
            className="mt-1 text-white rounded p-2 min-h-40"
            value={compatibilityReport}
            onChange={(e) => setCompatibilityReport(e.target.value)}
            placeholder="Paste the full compatibility report here. Line breaks will be preserved in the email."
          />
        </label>
        <label className="flex flex-col text-sm">Compatibility percentage
          <input
            className="mt-1 text-white rounded p-2"
            type="number"
            min="0"
            max="100"
            value={compatibilityPercentage}
            onChange={(e) => setCompatibilityPercentage(e.target.value)}
            placeholder="94"
          />
        </label>
        <div className="bg-gray-800 rounded p-3 text-sm text-gray-200">
          <p><strong>Email copy:</strong> Hello [First Name], {nameToInitials(selectedMan?.fullname1)} has just requested a Full Match Compatibility Report for the two of you. Your overall Match Compatibility is {compatibilityPercentage || '—'}%.</p>
        </div>
        <button className={`${compatibilityButtonClass} px-4 py-2 rounded transition-colors duration-200 disabled:opacity-60`} onClick={handleSendCompatibilityEmail} disabled={loadingCompatibilityEmail || !selectedMan || !selectedRecipientEmail || !compatibilityReport.trim() || !compatibilityPercentage.trim()}>
          {loadingCompatibilityEmail ? 'Sending email...' : 'Send compatibility email'}
        </button>
      </div>

      {preview ? (
        <div className="bg-gray-900 rounded shadow p-4 space-y-4">
          <h2 className="text-lg font-semibold">Confirm scheduled messages</h2>
          <div className="bg-gray-800 rounded p-3 text-sm space-y-1">
            <p><strong>Man:</strong> {manLabel(preview.man)}</p>
            <p><strong>Target:</strong> Women aged {preview.criteria.min_age}-{preview.criteria.max_age} in {preview.criteria.country}; packages: {preview.criteria.target_packages.join(', ')}</p>
            {preview.criteria.follow_up_only ? <p><strong>Follow up:</strong> Women who already have a previous conversation with this man.</p> : null}
            <p><strong>Message:</strong> {preview.message}</p>
            <p><strong>Recipients:</strong> {preview.recipients.length}</p>
            <p><strong>Selected:</strong> {selectedPreviewRecipients.length}</p>
            {parseEmailPaste(pastedConfirmEmails).length > 0 ? <p><strong>Pasted emails:</strong> {parseEmailPaste(pastedConfirmEmails).length} — only matching recipients from this preview will be scheduled.</p> : null}
          </div>
          <label className="flex flex-col text-sm">Paste recipient emails to create tasks for
            <textarea
              className="mt-1 text-white rounded p-2 min-h-28"
              value={pastedConfirmEmails}
              onChange={(e) => setPastedConfirmEmails(e.target.value)}
              placeholder="Paste emails here, one per line or separated by commas. They are still filtered by the selected age range, country, package, and follow-up criteria."
            />
          </label>
          <div className="flex gap-2 text-xs">
            <button className="bg-gray-700 px-2 py-1 rounded" type="button" onClick={selectAllPreviewRecipients}>Select all</button>
            <button className="bg-gray-700 px-2 py-1 rounded" type="button" onClick={clearPreviewRecipients}>Clear</button>
          </div>
          <div className="max-h-72 overflow-y-auto bg-gray-800 rounded p-3">
            {preview.recipients.map((recipient) => (
              <label key={recipient.email} className="flex items-center gap-2 border-b border-gray-700 py-1 text-sm">
                <input
                  type="checkbox"
                  checked={selectedPreviewRecipients.includes(recipient.email)}
                  onChange={() => togglePreviewRecipient(recipient.email)}
                />
                <span>{recipient.name} - {recipient.email} ({recipient.age}, {recipient.country}, {recipient.package})</span>
              </label>
            ))}
            {preview.recipients.length === 0 ? <p>No matching female users.</p> : null}
          </div>
          <div className="flex gap-2">
            <button className="bg-green-700 px-4 py-2 rounded" onClick={handleConfirm} disabled={loadingConfirm || (selectedPreviewRecipients.length === 0 && parseEmailPaste(pastedConfirmEmails).length === 0)}>
              {loadingConfirm ? 'Creating tasks...' : 'Confirm and create tasks'}
            </button>
            <button className="bg-gray-700 px-4 py-2 rounded" onClick={startAgain}>Start again</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
